import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  initGA,
  updateConsentMode,
  deleteGoogleAnalyticsCookies,
} from '../utils/googleAnalytics';
import { hasAnalyticsConsent } from '../utils/cookieUtils';

export const useAnalytics = () => {
  const location = useLocation();
  const previousConsentRef = useRef<boolean | null>(null);
  const isInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    // Function to handle consent changes
    const handleConsentChange = () => {
      const currentConsent = hasAnalyticsConsent();

      // Only act if consent status has changed
      if (previousConsentRef.current !== currentConsent) {
        // Update Google Consent Mode with current consent status
        updateConsentMode(currentConsent);

        // Initialize GA only when consent is granted for the first time
        if (currentConsent && !isInitializedRef.current) {
          initGA();
          isInitializedRef.current = true;
        }

        // If consent is withdrawn, clean up cookies aggressively
        if (!currentConsent && previousConsentRef.current === true) {
          // Immediate cleanup
          deleteGoogleAnalyticsCookies();
          // Multiple cleanup attempts to ensure all cookies are removed
          setTimeout(() => {
            deleteGoogleAnalyticsCookies();
          }, 100);
          setTimeout(() => {
            deleteGoogleAnalyticsCookies();
          }, 500);
          setTimeout(() => {
            deleteGoogleAnalyticsCookies();
          }, 1000);
        }

        previousConsentRef.current = currentConsent;
      }
    };

    // Check consent immediately
    handleConsentChange();

    // Listen for custom consent change events (more efficient than polling)
    const handleConsentEvent = () => {
      handleConsentChange();
    };

    window.addEventListener('cookieConsentChange', handleConsentEvent);

    // Fallback: Set up interval to check for consent changes (reduced frequency)
    const interval = setInterval(handleConsentChange, 5000);

    return () => {
      window.removeEventListener('cookieConsentChange', handleConsentEvent);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // Track page views on route changes (only if GA is initialized and consent is given)
    if (isInitializedRef.current && hasAnalyticsConsent()) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);
};
